// Message Templates Redux Slice

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { templatesApi } from '../../api/templatesApi';

// ─── Helpers ────────────────────────────────────────────────────────────────
const normalizeList = (data) => data?.templates ?? data?.results ?? data ?? [];
const unwrap = (data) => data?.template ?? data;

// ─── Async Thunks ──────────────────────────────────────────────────────────────

export const fetchTemplates = createAsyncThunk(
  'templates/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const res = await templatesApi.getList();
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to load templates');
    }
  }
);

export const createTemplate = createAsyncThunk(
  'templates/create',
  async (data, { rejectWithValue }) => {
    try {
      const res = await templatesApi.create(data);
      return unwrap(res.data);
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Failed to create template');
    }
  }
);

export const updateTemplate = createAsyncThunk(
  'templates/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await templatesApi.update(id, data);
      return unwrap(res.data);
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Failed to update template');
    }
  }
);

export const toggleTemplateActive = createAsyncThunk(
  'templates/toggleActive',
  async ({ id, is_active }, { rejectWithValue }) => {
    try {
      const res = await templatesApi.update(id, { is_active });
      return unwrap(res.data);
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to update status');
    }
  }
);

export const deleteTemplate = createAsyncThunk(
  'templates/delete',
  async (id, { rejectWithValue }) => {
    try {
      await templatesApi.remove(id);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to delete template');
    }
  }
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const templatesSlice = createSlice({
  name: 'templates',
  initialState: {
    list: [],
    count: 0,
    loading: false,
    submitting: false,
    error: null,
  },
  reducers: {
    clearError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    // fetchTemplates
    builder
      .addCase(fetchTemplates.pending,   (state) => { state.loading = true; state.error = null; })
      .addCase(fetchTemplates.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.list    = normalizeList(payload);
        state.count   = payload?.count ?? state.list.length;
      })
      .addCase(fetchTemplates.rejected,  (state, { payload }) => { state.loading = false; state.error = payload; });

    // createTemplate
    builder
      .addCase(createTemplate.pending,   (state) => { state.submitting = true; state.error = null; })
      .addCase(createTemplate.fulfilled, (state, { payload }) => {
        state.submitting = false;
        state.list.unshift(payload);
        state.count += 1;
      })
      .addCase(createTemplate.rejected,  (state, { payload }) => { state.submitting = false; state.error = payload; });

    // updateTemplate
    builder
      .addCase(updateTemplate.pending,   (state) => { state.submitting = true; state.error = null; })
      .addCase(updateTemplate.fulfilled, (state, { payload }) => {
        state.submitting = false;
        const idx = state.list.findIndex((t) => t.id === payload.id);
        if (idx !== -1) state.list[idx] = payload;
      })
      .addCase(updateTemplate.rejected,  (state, { payload }) => { state.submitting = false; state.error = payload; });

    // toggleTemplateActive (optimistic)
    builder
      .addCase(toggleTemplateActive.pending, (state, { meta }) => {
        const idx = state.list.findIndex((t) => t.id === meta.arg.id);
        if (idx !== -1) state.list[idx].is_active = meta.arg.is_active;
      })
      .addCase(toggleTemplateActive.fulfilled, (state, { payload }) => {
        const idx = state.list.findIndex((t) => t.id === payload.id);
        if (idx !== -1) state.list[idx] = payload;
      })
      .addCase(toggleTemplateActive.rejected, (state, { payload, meta }) => {
        const idx = state.list.findIndex((t) => t.id === meta.arg.id);
        if (idx !== -1) state.list[idx].is_active = !meta.arg.is_active;
        state.error = payload;
      });

    // deleteTemplate — permanent
    builder
      .addCase(deleteTemplate.pending,   (state) => { state.submitting = true; state.error = null; })
      .addCase(deleteTemplate.fulfilled, (state, { payload: id }) => {
        state.submitting = false;
        state.list  = state.list.filter((t) => t.id !== id);
        state.count = Math.max(0, state.count - 1);
      })
      .addCase(deleteTemplate.rejected,  (state, { payload }) => { state.submitting = false; state.error = payload; });
  },
});

export const { clearError } = templatesSlice.actions;
export default templatesSlice.reducer;
